import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import { Building2, Sun, Moon, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../lib/utils';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Loan Products', path: '/loan-products' },
  { name: 'Become Agent', path: '/become-agent' },
  { name: 'Contact', path: '/contact' },
];

import { Logo } from '../common/Logo';

export const PublicLayout = () => {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background transition-theme">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto h-full flex items-center justify-between px-4">
          <Link to="/" data-testid="public-logo">
            <Logo size="md" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  'px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  location.pathname === link.path
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                )}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="public-theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
            <Button asChild className="hidden md:flex" data-testid="public-login-btn">
              <Link to="/login">Login</Link>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-background border-b border-border shadow-lg">
            <nav className="flex flex-col p-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    'px-4 py-3 text-sm font-medium rounded-lg transition-colors',
                    location.pathname === link.path
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  )}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium rounded-lg bg-primary text-primary-foreground text-center mt-2"
              >
                Login
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <Link to="/" className="mb-4 block">
                <Logo size="md" imgClassName="object-contain brightness-0 invert" />
              </Link>
              <p className="text-slate-400 text-sm">
                Your trusted partner for all loan and financial services. We help you achieve your financial goals.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Our Services</Link></li>
                <li><Link to="/loan-products" className="hover:text-white transition-colors">Loan Products</Link></li>
                <li><Link to="/become-agent" className="hover:text-white transition-colors">Become an Agent</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Loan Types</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/apply/home-loan" className="hover:text-white transition-colors">Home Loan</Link></li>
                <li><Link to="/apply/personal-loan" className="hover:text-white transition-colors">Personal Loan</Link></li>
                <li><Link to="/apply/business-loan" className="hover:text-white transition-colors">Business Loan</Link></li>
                <li><Link to="/apply/car-loan" className="hover:text-white transition-colors">Car Loan</Link></li>
                <li><Link to="/apply/education-loan" className="hover:text-white transition-colors">Education Loan</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>1800-XXX-XXXX</li>
                <li>support@FinMatrixRQ.com</li>
                <li>123 Finance Street,<br />Mumbai, India 400001</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>&copy; {new Date().getFullYear()} FinMatrixRQ. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
